import { Box, Button, Stack } from "@mui/material";
import { toggleListItemExpand, useListContext, useRecordContext } from "ra-core";
import { FC, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useHasPermit } from "~/authProvider/useHasPermit";
import { EDIT_DATA_CLASSIFICATION_ENCRYPTION, MANAGE_DATA_CATALOG } from "~/constants";
import CreateOrEdit from "~/controller/CreateOrEdit";
import { useDefaultSavedHandlers } from "~/controller/defaultSavedHandlers";
import WithSave from "~/controller/WithSave";
import { ProtectionRuleDto, ProtectionRuleInput } from "~/gen/api/securefile";
import { DataProtectionRuleEdit, useDataProtectionRuleEditController } from "./DataProtectionRuleEdit";


const RowExpandDataProtectionRuleEdit: FC<{setRuleToBeDeleted: (r: ProtectionRuleDto) => void}> = ({setRuleToBeDeleted}) => {
  const record = useRecordContext()
  const dto: ProtectionRuleDto = record as any
  const dispatch = useDispatch()
  const { refetch } = useListContext()
  const hasClssPermit = useHasPermit([EDIT_DATA_CLASSIFICATION_ENCRYPTION])
  const hasEditPermit = useHasPermit([MANAGE_DATA_CATALOG])

  const closeExpanded = () => {
    dispatch(toggleListItemExpand('secure-files/data-protection-rules', dto.ruleId!))
  }

  const dialogRef = useRef<HTMLElement|null>(null)
  useEffect(() => {
    setTimeout(() => {
      dialogRef.current?.focus()
    })
  }, [])
  
  const ctrls = useDataProtectionRuleEditController()
  const { dirty, formDataValid, 
    states,
    ruleComposeCtrls: { validate: validateComposedRule, getComposedRule, states: { isDirty: ruleDefDirty } },
  } = ctrls

  const handlers = useDefaultSavedHandlers("Update Data Protection Rule")

  const handleSave = (save: (record:any,callbacks?:any)=>void) => async () => {
    if (!hasClssPermit && !hasEditPermit) { return }
    if (!dirty || !formDataValid) { return }

    const composedRuleValid = await validateComposedRule()
    if (!composedRuleValid) { return }
    const composedRule = getComposedRule()
    if (!composedRule) { return }
    const payload: ProtectionRuleInput = {
      ruleId: dto.ruleId,
      displayName: states.stage.displayName,
      comment: states.stage.comment,
      ruleDisplayTree: 
        ruleDefDirty ? JSON.stringify(composedRule) 
        : dto.ruleDisplayTree as any, // if rule def is not changed, this might save a rule set version bump
    }

    save(payload, {
      onSuccess: () => {
        refetch()
        closeExpanded()
      }
    })
  }

  return (
    <Box role="dialog">
      <span tabIndex={-1} ref={dialogRef} className="sr-only">Field Edit Region for {`${dto.displayName}`}</span>
      <Box sx={{ mb: 2, px: 4, }}>
        <DataProtectionRuleEdit {...ctrls} />
      </Box>
      <Stack direction="row" sx={{
        mb: -2, mx: -2, py: '10px', px: 5,
        borderTop: theme => `1px solid ${theme.palette.divider}`,
        bgcolor: 'background.default',
        '& button': { width: 96, mr: 2, },
      }}>
        {(hasClssPermit || hasEditPermit) &&
          <CreateOrEdit record={record} id={record.id} resource="secure-files/data-protection-rules" basePath="" {...handlers}>
            <WithSave 
              render={({save, saving}) =>
                <Button
                  disabled={!dirty || !formDataValid || saving}
                  variant="contained" 
                  size="small" 
                  color="primary"
                  onClick={handleSave(save)}
                >
                  Save
                </Button>
              }
            />
          </CreateOrEdit>}
        <Button
          variant="outlined" 
          size="small" 
          color="primary"
          onClick={closeExpanded}
        >
          Cancel
        </Button>
        <Button sx={{ ml: 'auto' }}
          variant="contained" 
          size="small" 
          color="error"
          onClick={() => setRuleToBeDeleted(dto)}
        >
          Delete
        </Button>
      </Stack>
    </Box>
  )
}

export default RowExpandDataProtectionRuleEdit;
